import React from "react";
import { Coins, Star, MapPin, Loader2, RefreshCw } from "lucide-react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { ProBadge } from "../../shared/ProBadge";

const FEATURED_COST = 5.0;

/**
 * RecorderPublishFormFields — the post-recording publish form:
 * Title / Location / Duration / Storage / Feature toggle. When a
 * commission is active, the Duration/Storage/Featured fields are
 * read-only because the EP has already paid for those parameters.
 *
 * Pure presentational; parent owns all state + setters. Location
 * comes from useRecorderLocation in the parent (Iter-58.11
 * surfaced it here as a visible row + retry button so the user
 * can see when geolocation fell back to Demo City and re-request).
 */
export const RecorderPublishFormFields = ({
  // Title
  videoTitle, setVideoTitle,
  // Location — Iter-58.11; setLocation added Jun 15, 2026 for the manual
  // city/country inputs that appear when GPS is unavailable.
  location, setLocation, locationError, locationLoading, getLocation,
  // Duration
  videoDuration, setVideoDuration, recordTimer, durationOptions, user,
  // Storage
  videoStorage, setVideoStorage, storageOptions,
  // Featured
  videoFeatured, setVideoFeatured,
  // Commission lock — when present, the EP has pre-paid these
  commission,
}) => (
  <>
    {/* Title */}
    <div>
      <Label className="text-gray-300 text-sm">Title *</Label>
      <Input
        value={videoTitle}
        onChange={(e) => setVideoTitle(e.target.value)}
        className="bg-gray-800 border-gray-700 text-white mt-1"
        placeholder="My amazing moment"
        data-testid="video-title-input"
      />
    </div>

    {/* Location — Iter-58.11 (2026-06-03) + Jun 15, 2026 update.
        Surface the captured city/country with a retry button. If GPS
        denied or fails, surface a manual city/country input so the user
        can publish without sharing precise location — but a real city
        IS required (backend rejects empty city with 400). */}
    <div data-testid="recorder-location-row">
      <Label className="text-gray-300 text-sm flex items-center gap-1.5 mb-1">
        <MapPin className="h-3.5 w-3.5" /> Location <span className="text-red-300/80 text-[10px]">(required)</span>
      </Label>
      <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm ${
        !location?.city || locationError
          ? "bg-amber-900/20 border-amber-500/40 text-amber-200"
          : "bg-gray-800/60 border-gray-700 text-gray-200"
      }`}>
        <div className="flex-1 min-w-0">
          {locationLoading ? (
            <span className="inline-flex items-center gap-1.5 text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Detecting location…
            </span>
          ) : (
            <span className="truncate" data-testid="recorder-location-display">
              {location?.city || "City required"}
              {location?.country ? `, ${location.country}` : ""}
              {!location?.city && (
                <span className="block text-[10px] mt-0.5 text-amber-300">
                  GPS unavailable. Tap Retry, or type a city below.
                </span>
              )}
              {locationError && (
                <span className="block text-[10px] mt-0.5 text-amber-300">
                  {locationError}
                </span>
              )}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={getLocation}
          disabled={locationLoading}
          data-testid="recorder-location-retry"
          aria-label="Re-detect location"
          title="Re-detect location"
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-900/60 hover:bg-gray-700 border border-gray-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${locationLoading ? "animate-spin" : ""}`} />
          Retry
        </button>
      </div>
      {(!location?.city || !location?.country) && setLocation && (
        <div className="grid grid-cols-2 gap-2 mt-2" data-testid="recorder-manual-location">
          <Input
            value={location?.city || ""}
            onChange={(e) => setLocation({
              ...(location || { lat: 0, lng: 0 }),
              city: e.target.value,
            })}
            placeholder="City (e.g. Oklahoma City)"
            className="bg-gray-800 border-gray-700 text-white text-sm"
            data-testid="recorder-manual-city"
          />
          <Input
            value={location?.country || ""}
            onChange={(e) => setLocation({
              ...(location || { lat: 0, lng: 0 }),
              country: e.target.value,
            })}
            placeholder="Country (e.g. United States)"
            className="bg-gray-800 border-gray-700 text-white text-sm"
            data-testid="recorder-manual-country"
          />
        </div>
      )}
    </div>

    {/* Duration */}
    <div>
      <Label className="text-gray-300 text-sm mb-2 block">
        Video Duration
        <span className="text-gray-500 text-[10px] ml-2">
          (you recorded {recordTimer}s — pick the smallest bracket that fits)
        </span>
      </Label>
      {commission ? (
        <div className="bg-gray-800/60 border border-amber-500/30 rounded-lg p-2 text-xs text-amber-200" data-testid="commission-duration-locked">
          Locked by commission: <span className="font-semibold">{videoDuration}s</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {durationOptions.map((opt) => {
            const isProducer = (user?.subscription_tier || "").toLowerCase() === "producer";
            const tierLocked = opt.producerOnly && !isProducer;
            const disabled = recordTimer > opt.value || tierLocked;
            return (
              <button
                key={opt.value}
                onClick={() => !disabled && setVideoDuration(opt.value)}
                disabled={disabled}
                className={`relative p-2 rounded-lg border text-sm transition-all ${
                  videoDuration === opt.value
                    ? "bg-emerald-600 border-emerald-500 text-white"
                    : disabled
                      ? "bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed"
                      : opt.producerOnly
                        ? "bg-amber-900/30 border-amber-700/50 text-amber-200 hover:border-amber-500"
                        : "bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600"
                }`}
                data-testid={`duration-${opt.value}`}
                title={
                  tierLocked
                    ? "9-minute videos require a Producer subscription"
                    : disabled
                      ? `Your recording is longer than ${opt.value}s`
                      : ""
                }
              >
                {opt.producerOnly && (
                  <span className="absolute -top-1.5 -right-1.5">
                    <ProBadge unlocked={isProducer} testId={`producer-badge-${opt.value}`} />
                  </span>
                )}
                <div className="font-semibold">{opt.label}</div>
                <div className="text-[10px] opacity-75">
                  {opt.extra === 0 ? "included" : `+${opt.extra.toFixed(2)}`}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>

    {/* Storage */}
    <div>
      <Label className="text-gray-300 text-sm mb-2 block">Storage Duration</Label>
      {commission ? (
        <div className="bg-gray-800/60 border border-amber-500/30 rounded-lg p-2 text-xs text-amber-200" data-testid="commission-storage-locked">
          Locked by commission: <span className="font-semibold">{videoStorage} days</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {storageOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setVideoStorage(opt.value)}
              className={`p-2 rounded-lg border text-sm transition-all ${
                videoStorage === opt.value
                  ? "bg-cyan-600 border-cyan-500 text-white"
                  : "bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600"
              }`}
              data-testid={`storage-${opt.value}`}
            >
              <div className="font-semibold">{opt.label}</div>
              <div className="text-[10px] opacity-75">
                {opt.extra === 0 ? "included" : `+${opt.extra.toFixed(2)}`}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>

    {/* Feature toggle */}
    <div>
      <label className="text-sm font-medium text-gray-300 mb-2 block">Promotion</label>
      {commission ? (
        <div className="bg-gray-800/60 border border-amber-500/30 rounded-lg p-2 text-xs text-amber-200" data-testid="commission-featured-locked">
          {videoFeatured ? "Locked by commission: Featured" : "Locked by commission: not featured"}
        </div>
      ) : (() => {
        const canAffordFeature = user && user.credits >= FEATURED_COST;
        return (
          <button
            onClick={() => { if (canAffordFeature) setVideoFeatured(!videoFeatured); }}
            disabled={!canAffordFeature && !videoFeatured}
            title={!canAffordFeature ? "Need 5.00 credits — top up" : "Toggle featured promotion"}
            className={`w-full p-3 rounded-lg border text-left transition-all ${
              !canAffordFeature && !videoFeatured
                ? "bg-gray-900/40 border-amber-500/30 text-gray-500 cursor-not-allowed"
                : videoFeatured
                  ? "bg-amber-600/20 border-amber-500 text-white"
                  : "bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600"
            }`}
            data-testid="feature-video-toggle"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold flex items-center gap-2">
                  {!canAffordFeature && !videoFeatured
                    ? <Coins className="h-4 w-4 text-amber-400" />
                    : <Star className={`h-4 w-4 ${videoFeatured ? "text-amber-400" : "text-gray-500"}`} />}
                  Feature this video
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {!canAffordFeature && !videoFeatured
                    ? "Need 5.00 credits — top up to unlock"
                    : "Appear in \"Featured Videos\" boxes on homepage, feed & profiles"}
                </p>
              </div>
              <span className={`text-sm font-semibold ${videoFeatured ? "text-amber-400" : "text-gray-500"}`}>
                +5.00 credits
              </span>
            </div>
          </button>
        );
      })()}
    </div>
  </>
);
